import { useMemo } from 'react';
import { Spinner } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import AIMarker from 'components/elements/AIMarker';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import CompanyContact from 'components/widgets/DocumentModals/CompanyContact';
import ContactsFromAI from 'components/widgets/DocumentModals/ContactsFromAI';
import { useEnrichContactsUnlocked } from 'hooks/useEnrichContactsUnlocked';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { type DocumentRelatedContactSuggestionsResponse } from 'services/API/AIInteractionAPI';
import { CompanyItem } from 'types';
import { FLAGS } from 'utils/featureFlags';
import { formatCompanyName } from 'utils/format';
import type { DocumentContact } from '@mercator-gcp/types/backend/DocumentContact';

type ContactWithCompanyDetails = DocumentContact & {
  companyDetails?: CompanyItem;
};

type RenderContactsProps = {
  contact: ContactWithCompanyDetails;
  documentId?: string;
  isLoading: boolean;
  error?: string;
  aiSearchResult?: DocumentRelatedContactSuggestionsResponse;
  /** When true, reveal available AI results without requiring a manual unlock. */
  autoReveal?: boolean;
  onSearch: () => void;
};

const contactTypeStyle = {
  textTransform: 'capitalize',
};

const contactItemStyle = {
  width: '100%',
  gap: 'padding_8',
};

const contactContainerStyle = {
  width: '100%',
  alignItems: 'flex-start',
};

const itemContainer = {
  gap: 'padding_4',
};

const successMessageLabelStyle = {
  color: Colors.TextQuiet,
  gap: 'padding_4',
};

const Contacts = ({
  contact,
  documentId,
  isLoading,
  error,
  aiSearchResult,
  autoReveal = false,
  onSearch,
}: RenderContactsProps) => {
  const { isFlagEnabled } = useFeatureFlags();
  const { isUnlocked: hasUnlockedAiResults, markUnlocked } = useEnrichContactsUnlocked(
    documentId,
    contact.id ?? undefined,
  );
  const showAiResults = !!aiSearchResult && (hasUnlockedAiResults || autoReveal);

  const getCompanyName = () => {
    if (!contact.company) {
      return null;
    }
    if (contact.companyDetails) {
      return <CompanyContact company={contact.companyDetails} />;
    }

    return <Text.BodyBold>{formatCompanyName(contact.company)}</Text.BodyBold>;
  };

  const matchesFoundLabel = useMemo(() => {
    if (!showAiResults || !aiSearchResult) {
      return '';
    }

    const count = aiSearchResult.contacts.length;
    if (count === 1) {
      return `${count} match found`;
    }

    return `${count} matches found`;
  }, [aiSearchResult, showAiResults]);

  return (
    <FlexColumn sx={contactItemStyle}>
      <FlexRow sx={contactContainerStyle}>
        <FlexColumn sx={itemContainer}>
          {getCompanyName()}
          {contact.type && <Text sx={contactTypeStyle}>{contact.type}</Text>}
          {contact.name && <Text sx={contactTypeStyle}>{contact.name}</Text>}
          {contact.phone && <Text>{contact.phone}</Text>}
          {contact.email && <Text>{contact.email}</Text>}

          {error && (
            <Text
              sx={{
                color: Colors.SystemColorNegative,
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {error}
            </Text>
          )}
        </FlexColumn>
        {isFlagEnabled(FLAGS.ENRICH_CONTACTS) &&
          (!contact.email || !contact.phone) &&
          contact.id &&
          !!documentId &&
          !showAiResults &&
          (isLoading ? (
            <FlexRow sx={successMessageLabelStyle}>
              <Spinner size="xs" thickness="2px" speed="0.65s" color={Colors.TextQuiet} />
              <Text>Finding contact info…</Text>
            </FlexRow>
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                markUnlocked();
                if (!aiSearchResult) {
                  onSearch();
                }
              }}
              startAdornment={<Icon name={Icon.Name.WorldWeb} />}
            >
              Search for contact info
            </Button>
          ))}
        {showAiResults && (
          <FlexRow sx={successMessageLabelStyle}>
            <AIMarker />
            {matchesFoundLabel}
          </FlexRow>
        )}
      </FlexRow>
      {showAiResults && documentId && contact.id && (
        <ContactsFromAI contacts={aiSearchResult.contacts} />
      )}
    </FlexColumn>
  );
};

export default Contacts;
